import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";

const SidebarCreditDisplay = ({ className = "" }: { className?: string }) => {
  const { user } = useAuth();
  const { subscription, loading, isFetching, creditsData, openCustomerPortal } = useSubscription();
  const [showPricingModal, setShowPricingModal] = useState(false);

  const handleUpgradeClick = async () => {
    // For trial users, open customer portal to add payment method
    if (subscription?.subscription_status === 'trialing' && !subscription?.payment_method_added) {
      const origin = window.location.origin;
      const flowData = {
        type: 'payment_method_update',
        after_completion: {
          type: 'redirect',
          redirect: {
            return_url: `${origin}/`
          }
        }
      };
      
      try {
        await openCustomerPortal(flowData);
      } catch (error) {
        console.error('Error opening customer portal:', error);
        // Fallback - could open pricing modal if needed
      }
    } else {
      // Open pricing modal or customer portal for other cases
      try {
        await openCustomerPortal();
      } catch (error) {
        console.error('Error opening customer portal:', error);
      }
    }
  };

  return (
    <div className={`flex items-center justify-center gap-2 px-2 py-1 border border-b-0 rounded-2xl relative h-8 ${
      // Orange styling for trial days 13-14 without payment method
      subscription?.isOnTrial && subscription?.trialDaysRemaining && subscription.trialDaysRemaining <= 2 && !subscription.payment_method_added
        ? 'border-orange-300 bg-orange-50'
        : 'border-[#DADAD9]'
    } ${className}`}>
      <img src="/lovable-uploads/3dc23cd9-8051-4e9c-b14f-9d3ee05ae883.png" alt="Credits" className="h-4 w-auto" />
      <span className="text-sm font-medium text-gray-900">
        {(loading || isFetching || !user) ? '...' : (creditsData?.credits_left ?? 0)}
      </span>
      {creditsData && (
        <>
          <Separator orientation="vertical" className="h-4 mx-1" />
          <span className="text-sm font-medium text-gray-900">
            {(() => {
              // Pre-Trial States - hide if no creditsData record
              if (!creditsData) return null;
              
              // Trial States
              if (subscription?.isOnTrial && subscription?.trialDaysRemaining !== undefined) {
                const daysLeft = subscription.trialDaysRemaining;
                
                // Trial days 13-14 WITHOUT payment method
                if (daysLeft <= 2 && !subscription.payment_method_added) {
                  return (
                    <Button variant="link" size="sm" onClick={handleUpgradeClick} className="text-orange-600 hover:text-orange-700 p-0 h-6 font-medium rounded-2xl">
                      ⚠ Add Payment Method ({daysLeft} {daysLeft === 1 ? 'day' : 'days'} left)
                    </Button>
                  );
                }
                
                // Trial any day WITH or WITHOUT payment method (but not in warning period)
                return `Trial: ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
              }
              
              // Active Subscription States
              if (subscription?.subscription_status === 'active' && subscription?.subscription_tier) {
                return subscription.subscription_tier;
              }
              
              // Canceled State (still within paid period)
              if (subscription?.isCanceled && subscription?.subscription_end_date && new Date(subscription.subscription_end_date) > new Date()) {
                const endDate = new Date(subscription.subscription_end_date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                });
                return `${subscription.subscription_tier || 'Plan'} (Ends ${endDate})`;
              }
              
              // Inactive States
              if (subscription?.isTrialExpired || subscription?.hasPaymentIssues || 
                  (subscription?.isCanceled && subscription?.subscription_end_date && new Date(subscription.subscription_end_date) <= new Date())) {
                return (
                  <Button variant="link" size="sm" onClick={handleUpgradeClick} className="text-blue-600 hover:text-blue-700 p-0 h-6 font-medium rounded-2xl">
                    Reactivate Plan
                  </Button>
                );
              }
              
              // Loading/Error States
              if (loading) {
                return 'Loading...';
              }
              
              // Default upgrade button
              return (
                <Button variant="link" size="sm" onClick={handleUpgradeClick} className="text-blue-600 hover:text-blue-700 p-0 h-6 font-medium rounded-2xl">
                  Upgrade
                </Button>
              );
            })()}
          </span>
        </>
      )}
    </div>
  );
};

export default SidebarCreditDisplay;
